import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { PencilAlt } from '@styled-icons/heroicons-outline/PencilAlt'
import { User } from '@styled-icons/heroicons-outline/User'
import React, { useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { useActivateDeactivateLocationsMutation } from 'api/location/activateDeactivateLocations'
import { useLazyGetAllLocationNotesQuery, usePatchLocationNoteMutation } from 'api/notes/locationNotesCrud'
import { ReactComponent as LocationNotesIcon } from 'assets/LocationNotesIcon.svg'
import { ReactComponent as VacationIcon } from 'assets/services/hold/VacationIcon.svg'
import Loader from 'components/common/loader'
import ToggleLocationStatusMenuItem from 'components/common/locations/ToggleLocationStatusMenuItem'
import useLocationVacationHolds from 'components/customer-details/content/location/list/hooks/useLocationVacationHolds'
import VacationHoldDialog from 'components/customers/accounts/vacation-holds/VacationHoldDialog'
import ViewArchivedNotesMenuOption from 'components/notes/common/actions/ViewArchivedNotesMenuOption'
import StyledBadge from 'components/notes/common/StyledBadge'
import ViewArchiveNotesDialog from 'components/notes/common/ViewArchiveNotesDialog'
import LocationNotesDialogVariant from 'components/notes/location/LocationNotesDialogVariant'
import { getArchivedAndUnarchivedNotes, getSortedNotes } from 'data/notes/notesSelectors'
import T from 'T'
import { handleError } from 'utils/error'

import AddEditLocationDialog from './add-edit-dialog'
import LocationContactsDialog from './list/components/LocationContactsDialog'

import type { Address } from 'types/entities/Address'

export type ActionMenuLocation = {
  id?: string
  address?: Partial<Address>
  contacts?: unknown[]
  tags?: unknown[]
  deactivated?: boolean
  serviceCount?: number
  archivedNotesCount?: number
  notesCount?: number
  signatureRequired?: boolean
  parcelNumber?: string
  po?: string
  maxPoAmount?: string | number
}

type LocationActionMenuProps = {
  anchorEl?: HTMLElement | null
  selectedLocation?: ActionMenuLocation | null
  isAccountActive?: boolean
  showArchivedNotesOption?: boolean
  accountId: string
  onClose: () => void
  onLocationStatusChange: () => void
}

type LocationDialogModel = {
  id?: string
  address: Partial<Address>
  contacts: unknown[]
  tags: unknown[]
  signatureRequired: boolean
  deactivated: boolean
  parcelNumber: string
  locationPurchaseOrder: boolean
  po?: string
  maxPoAmount?: string | number
}

const LocationActionMenu = ({
  anchorEl,
  selectedLocation = {},
  isAccountActive = true,
  showArchivedNotesOption = false,
  accountId,
  onClose,
  onLocationStatusChange,
}: LocationActionMenuProps) => {
  const [isOpenAddEditLocationDialog, setIsOpenAddEditLocationDialog] = useState(false)
  const [isOpenViewArchiveNoteDialog, setIsOpenViewArchiveNoteDialog] = useState(false)
  const [isOpenViewContactsDialog, setIsOpenViewContactsDialog] = useState(false)
  const [isOpenViewNotesDialog, setIsOpenViewNotesDialog] = useState(false)
  const [isOpenVacationHoldDialog, setIsOpenVacationHoldDialog] = useState(false)
  const [locationForDialog, setLocationForDialog] = useState<LocationDialogModel | null>(null)
  const [selectedLocationData, setSelectedLocationData] = useState<ActionMenuLocation | null>(null)
  const [activateDeactivateLocations, { isLoading: isActivateDeactivateLocationsLoading }] = useActivateDeactivateLocationsMutation()
  const [getAllLocationNotes, { isFetching: isGetAllNotesLoading, data: notesData }] = useLazyGetAllLocationNotesQuery()
  const { getVacationHoldCountForLocation } = useLocationVacationHolds(accountId)

  const locationId = selectedLocation?.id
  const contacts = selectedLocation?.contacts ?? []
  const isDeactivated = selectedLocation?.deactivated ?? false
  const hasActiveServices = (selectedLocation?.serviceCount ?? 0) > 0
  const sortedNotes = getSortedNotes({ notes: notesData?.notes ?? [] })
  const { archivedNotes } = getArchivedAndUnarchivedNotes({ notes: sortedNotes })
  const archivedNotesCount = selectedLocation?.archivedNotesCount ?? 0
  const notesCount = selectedLocation?.notesCount ?? 0
  const vacationHoldsCount = getVacationHoldCountForLocation(locationId)

  const getLocationModel = useMemo((): LocationDialogModel => {
    const po = selectedLocation?.po
    const maxPoAmount = selectedLocation?.maxPoAmount
    const hasPurchaseOrder = Boolean(po || maxPoAmount)

    return {
      id: locationId,
      address: selectedLocation?.address ?? {},
      contacts: selectedLocation?.contacts ?? [],
      tags: selectedLocation?.tags ?? [],
      signatureRequired: selectedLocation?.signatureRequired ?? false,
      deactivated: isDeactivated,
      parcelNumber: selectedLocation?.parcelNumber ?? '',
      locationPurchaseOrder: hasPurchaseOrder,
      po: hasPurchaseOrder ? po : '',
      maxPoAmount: hasPurchaseOrder ? maxPoAmount : '',
    }
  }, [locationId, selectedLocation, isDeactivated])

  const handleLocationActivateDeactivate = useCallback(() => {
    const payload = {
      deactivate: !isDeactivated,
      locations: [{ id: locationId }],
    }

    activateDeactivateLocations(payload)
      .unwrap()
      .then(() => {
        onClose()
        onLocationStatusChange()
        toast.success(payload.deactivate ? T.LOCATION_DEACTIVATED_MSG : T.LOCATION_ACTIVATED_MSG)
      })
      .catch(handleError)
  }, [isDeactivated, locationId, activateDeactivateLocations, onClose, onLocationStatusChange])

  const handleViewArchiveClick = useCallback(() => {
    setLocationForDialog(getLocationModel)
    setIsOpenViewArchiveNoteDialog(true)
    getAllLocationNotes({ id: locationId ?? '' })
      .unwrap()
      .catch(error => {
        handleError(error)
        setIsOpenViewArchiveNoteDialog(false)
      })
    onClose()
  }, [getLocationModel, getAllLocationNotes, locationId, onClose])

  const handleCloseViewArchiveDialog = useCallback(() => setIsOpenViewArchiveNoteDialog(false), [])

  const handleViewContactsClick = useCallback(() => {
    setSelectedLocationData(selectedLocation)
    setIsOpenViewContactsDialog(true)
    onClose()
  }, [selectedLocation, onClose])

  const handleCloseViewContactsDialog = useCallback(() => {
    setIsOpenViewContactsDialog(false)
    setSelectedLocationData(null)
  }, [])

  const handleViewNotesClick = useCallback(() => {
    setSelectedLocationData(selectedLocation)
    setIsOpenViewNotesDialog(true)
    onClose()
  }, [selectedLocation, onClose])

  const handleCloseViewNotesDialog = useCallback(() => {
    setIsOpenViewNotesDialog(false)
    setSelectedLocationData(null)
  }, [])

  const handleVacationHoldClick = useCallback(() => {
    setLocationForDialog(getLocationModel)
    setIsOpenVacationHoldDialog(true)
    onClose()
  }, [getLocationModel, onClose])

  const handleCloseVacationHoldDialog = useCallback(() => {
    setIsOpenVacationHoldDialog(false)
    setSelectedLocationData(null)
  }, [])

  const handleEditClick = useCallback(() => {
    setLocationForDialog(getLocationModel)
    setIsOpenAddEditLocationDialog(true)
    onClose()
  }, [getLocationModel, onClose])

  const handleCloseEditDialog = useCallback(() => {
    setIsOpenAddEditLocationDialog(false)
    setLocationForDialog(null)
  }, [])

  return (
    <>
      {isActivateDeactivateLocationsLoading && <Loader />}
      <Menu
        disableScrollLock
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        slotProps={{ paper: { variant: 'outlined', sx: { minWidth: showArchivedNotesOption ? 275 : 200 } } }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={onClose}
      >
        <MenuItem disabled={contacts.length === 0} onClick={handleViewContactsClick}>
          <ListItemIcon sx={{ mr: 1 }}>
            <StyledBadge badgeContent={contacts.length}>
              <User height={24} width={24} />
            </StyledBadge>
          </ListItemIcon>
          <ListItemText>{T.LOCATION_CONTACTS}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleViewNotesClick}>
          <ListItemIcon sx={{ mr: 1 }}>
            <StyledBadge badgeContent={notesCount}>
              <LocationNotesIcon height={24} width={24} />
            </StyledBadge>
          </ListItemIcon>
          <ListItemText>{T.LOCATION_NOTES}</ListItemText>
        </MenuItem>
        {showArchivedNotesOption && (
          <ViewArchivedNotesMenuOption count={archivedNotesCount} isDisabled={archivedNotesCount === 0} onClick={handleViewArchiveClick} />
        )}
        <MenuItem onClick={handleVacationHoldClick}>
          <ListItemIcon sx={{ mr: 1 }}>
            <StyledBadge badgeContent={vacationHoldsCount}>
              <VacationIcon height={24} width={24} />
            </StyledBadge>
          </ListItemIcon>
          <ListItemText>{T.HOLDS}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon sx={{ mr: 1 }}>
            <PencilAlt height={24} width={24} />
          </ListItemIcon>
          <ListItemText>{T.EDIT_LOCATION}</ListItemText>
        </MenuItem>
        <ToggleLocationStatusMenuItem
          hasActiveServices={hasActiveServices}
          isAccountActive={isAccountActive}
          isLocationDeactivated={isDeactivated}
          onClick={handleLocationActivateDeactivate}
        />
      </Menu>

      <AddEditLocationDialog
        accountId={accountId}
        isOpen={isOpenAddEditLocationDialog}
        location={locationForDialog}
        onClose={handleCloseEditDialog}
      />
      <ViewArchiveNotesDialog
        id={locationForDialog?.id ?? ''}
        isLoading={isGetAllNotesLoading}
        isOpen={isOpenViewArchiveNoteDialog}
        notes={archivedNotes}
        title={T.ARCHIVED_LOCATION_NOTES}
        useUnarchiveNoteMutation={usePatchLocationNoteMutation}
        onClose={handleCloseViewArchiveDialog}
      />
      <LocationContactsDialog
        isOpen={isOpenViewContactsDialog}
        selectedLocation={selectedLocationData ?? undefined}
        onClose={handleCloseViewContactsDialog}
      />
      <LocationNotesDialogVariant
        isOpen={isOpenViewNotesDialog}
        locationId={selectedLocationData?.id}
        onClose={handleCloseViewNotesDialog}
      />
      <VacationHoldDialog
        open={isOpenVacationHoldDialog}
        row={{ accountId, locationId: selectedLocationData?.id }}
        onClose={handleCloseVacationHoldDialog}
      />
    </>
  )
}

export default LocationActionMenu
